<template>
	<div class="fd-filter" v-if="visible" :class="clazz" :style="style">
		<array-part :data="value" :part="arrayPart" :readonly="ro" :viewmode="vm" class="fd-filter-array" ref="arrayComponent"></array-part>
	</div>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor";
import type {FilterItemPartType, FilterPart} from "@t/fd/editor/Filter";
import {computed, ref, onMounted} from "vue";
import ArrayPart from "@fd/containers/ArrayPart.vue";
import type {Array, ArrayAddButton} from "@t/fd/layout/Array";
import {injectEnv} from "@fd/Environment";
import type {FilterEntry, FilterGroup, FilterNested} from "@t/agenda/Filter";
import {useFilter } from "@fd/editors/filter/FilterUtils";
import type {Text} from "@t/fd/fixed/Text.ts";

const uprops = defineProps({
	...editorProps
});

const props = uprops as EditorProps;
const fp = computed<FilterPart>(() => props.part as FilterPart);
const {visible, value, clazz, style, ro, vm} = useEditor(props);
const env = injectEnv();
const { registration } = useFilter(fp);
const arrayComponent = ref(null);

function discardConditions() {
	if (arrayComponent.value) {
		arrayComponent.value.discardItems();
	}
}

const arrayPart = computed<Array>(() => {
    const arrayPart:Array = {
        type: "array",
        sortable: true,
		autoIdentity: true,
		objectMode: true,
        property: "conditions",
        add: [
            <ArrayAddButton>{
                label: env.msg("butt.addCondition.F"),
                init: "value",
                value: <FilterEntry>{
                    type: "F",
                    join: "AND",
					values: []
                }
            },
            <ArrayAddButton>{
                label: env.msg("butt.addCondition.G"),
                init: "value",
                value: <FilterGroup>{
                    type: "G",
                    join: "AND",
                    conditions: [
						<FilterEntry>{
							type: "F",
							join: "AND",
							values: []
						}
					]
                }
            }
        ],
        item: <FilterItemPartType>{ type: "filter-item" },
		placeholder: <Text>{
			type: "text",
			value: env.msg('form.noRules'),
			options: {
				clazz: ['filter-empty']
			}
		}
    };

	// Check if current agenda has any references (referencing agendas)
    if (registration.value != null
		&& registration.value.type		// type of referencing columns
		&& Array.isArray(registration.value.references) && registration.value.references.length > 0) {
        arrayPart.add?.push(<ArrayAddButton>{
            label: env.msg("butt.addCondition.N"),
			init: "value",
            value: <FilterNested>{
				join: "AND",
				type: "N",
				op: 14,
				agenda: {
					view: {
						filter: { conditions: [] }
					}
				}
			}
        });
    }

    return arrayPart;
});

defineExpose({ discardConditions });

// onMounted(() => {
// 	console.log("Filter editor mounted with this descriptor:");
// 	console.log(fp.value.descriptor);
// });
</script>

<style>
.fd-filter-array {
	row-gap: 4px;
}
.filter-empty {
	font-size: var(--font-size-xl);
	line-height: 32px;	/* in sync with the height of single line filter item (22px inpuit + 2*4px padding + 2px border)=32px */
	font-weight: 300;
	display: block;
	opacity: 0.5;
}
</style>
